import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  integrations: {
    pms_import_email?: string | null;
    tourist_integration?: { pms_import_email?: string | null };
  };
}

interface PmsIntegrationPanelProps {
  canWrite: boolean;
}

export function PmsIntegrationPanel({ canWrite }: PmsIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pmsImportEmail, setPmsImportEmail] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((settings) => {
        const integ = settings.integrations;
        setPmsImportEmail(
          integ?.tourist_integration?.pms_import_email ?? integ?.pms_import_email ?? "",
        );
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch("/api/v1/settings/integrations", {
        ...(pmsImportEmail.trim()
          ? { pms_import_email: pmsImportEmail.trim() }
          : { clear_pms_import_email: true }),
      });
      setSuccess(true);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <form onSubmit={(e) => void onSave(e)} className="space-y-5">
      <FormHint>
        Bei jeder Online-Buchung wird eine PMS-Import-XML an die hinterlegte E-Mail-Adresse
        gesendet. Ihr Property-Management-System importiert die Reservierung automatisch.
      </FormHint>

      <fieldset className="space-y-4" disabled={!canWrite}>
        <label className="block min-w-0 text-sm">
          <span className="font-medium text-label">PMS-Import E-Mail (XML)</span>
          <input
            type="email"
            className="eh-input mt-1.5 max-w-md"
            value={pmsImportEmail}
            onChange={(e) => setPmsImportEmail(e.target.value)}
            placeholder="pms-import@example.com"
            autoComplete="off"
          />
          <span className="mt-1 block text-xs text-label-secondary">
            Empfänger für die PMS-Import-XML bei jeder Online-Buchung.
          </span>
        </label>
      </fieldset>

      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {success ? <p className="text-sm text-status-green">Einstellungen gespeichert.</p> : null}
      {canWrite ? (
        <div className="flex justify-end border-t border-separator pt-4">
          <Button type="submit" loading={saving}>
            Speichern
          </Button>
        </div>
      ) : null}
    </form>
  );
}
